import React from "react";
import L from "leaflet";
import { Circle, CircleMarker, MapContainer, Marker, Polyline, Popup, TileLayer, useMap, useMapEvents } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { DEFAULT_CENTER } from "./navigationRebuildUtils";

const PENCIL_DOT_MARKER_ICON = L.divIcon({
  className: "",
  html: `<div style="width:30px;height:30px;border-radius:9999px;background:#0f766e;color:white;border:3px solid white;box-shadow:0 10px 24px rgba(15,23,42,.35);display:flex;align-items:center;justify-content:center;font-weight:900;font-size:16px;line-height:1;">✎</div><div style="position:absolute;left:12px;top:12px;width:6px;height:6px;border-radius:9999px;background:white;"></div>`,
  iconSize: [30, 30],
  iconAnchor: [15, 15],
  popupAnchor: [0, -15],
});

function MapEvents({ layers, onCursor, onAddWaypoint }) {
  useMapEvents({
    mousemove(event) {
      onCursor([event.latlng.lat, event.latlng.lng]);
    },
    click(event) {
      if (layers.route) onAddWaypoint([event.latlng.lat, event.latlng.lng]);
    },
  });
  return null;
}

function GridOverlay() {
  const lines = [];
  for (let lat = -80; lat <= 80; lat += 5) lines.push([[lat, -180], [lat, 180]]);
  for (let lng = -180; lng <= 180; lng += 5) lines.push([[-80, lng], [80, lng]]);
  return lines.map((line, index) => <Polyline key={index} positions={line} pathOptions={{ color: "#0f766e", weight: 1, opacity: 0.18 }} />);
}

function MapResizeFix() {
  const map = useMap();
  React.useEffect(() => {
    const refresh = () => map.invalidateSize({ pan: false });
    const timers = [100, 350, 900, 1600].map((delay) => window.setTimeout(refresh, delay));
    return () => timers.forEach((timer) => window.clearTimeout(timer));
  }, [map]);
  return null;
}

function UserLocationCenter({ point }) {
  const map = useMap();
  const centeredRef = React.useRef(false);

  React.useEffect(() => {
    if (!point || centeredRef.current) return;
    centeredRef.current = true;
    map.setView(point, Math.max(map.getZoom(), 12), { animate: true });
  }, [map, point]);

  return null;
}

function CenterOnUserAction({ point, requestKey }) {
  const map = useMap();

  React.useEffect(() => {
    if (!point || !requestKey) return;
    map.setView(point, Math.max(map.getZoom(), 13), { animate: true });
  }, [map, point, requestKey]);

  return null;
}

function projectPoint([lat, lng], bearingDegrees, distanceNmValue) {
  const distanceDegrees = distanceNmValue / 60;
  const bearing = (bearingDegrees * Math.PI) / 180;
  return [lat + Math.cos(bearing) * distanceDegrees, lng + Math.sin(bearing) * distanceDegrees];
}

export default function RebuildMapCanvas({ layers, marinas, routePoints, telemetry, vesselTrace = [], centerRequestKey, onCursor, onAddWaypoint, onDeleteWaypoint }) {
  const gps = telemetry;
  const vesselColor = gps?.isEstimated ? "#f59e0b" : "#0891b2";
  const accuracyColor = gps?.isEstimated ? "#f59e0b" : "#06b6d4";
  const vesselIcon = React.useMemo(() => L.divIcon({
    className: "",
    html: `<div style="width:34px;height:34px;border-radius:9999px;background:white;box-shadow:0 12px 28px rgba(15,23,42,.38);display:flex;align-items:center;justify-content:center;border:2px solid ${vesselColor};"><svg width="23" height="23" viewBox="0 0 24 24" fill="none" style="transform:rotate(${gps?.heading || 0}deg);transform-origin:50% 50%;filter:drop-shadow(0 1px 1px rgba(15,23,42,.25));"><path d="M12 2.75L19.25 20.25L12 16.75L4.75 20.25L12 2.75Z" fill="${vesselColor}" stroke="white" stroke-width="1.5" stroke-linejoin="round"/><path d="M12 6.75V16.25" stroke="white" stroke-width="1.6" stroke-linecap="round"/></svg></div>`,
    iconSize: [34, 34],
    iconAnchor: [17, 17],
    popupAnchor: [0, -17],
  }), [vesselColor, gps?.heading]);

  const headingLine = gps ? [gps.point, projectPoint(gps.point, gps.heading || 0, 0.6)] : null;
  const courseLine = gps ? [gps.point, projectPoint(gps.point, gps.heading || 35, 1.2)] : null;
  const activeRoutePoints = gps && routePoints.length ? [gps.point, ...routePoints] : routePoints;

  return (
    <MapContainer
      center={DEFAULT_CENTER}
      zoom={10}
      minZoom={3}
      maxZoom={18}
      preferCanvas
      className="!h-full !min-h-[620px] !w-full !bg-sky-100"
      style={{ height: "100%", minHeight: "620px", width: "100%", background: "#dbeafe" }}
      scrollWheelZoom
      zoomControl
    >
      <TileLayer
        attribution="&copy; OpenStreetMap contributors"
        url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
        maxZoom={19}
        detectRetina
        updateWhenIdle
        updateWhenZooming={false}
        keepBuffer={5}
      />

      {layers.seamarks && (
        <TileLayer
          attribution="Seamarks &copy; OpenSeaMap"
          url="https://tiles.openseamap.org/seamark/{z}/{x}/{y}.png"
          opacity={0.95}
          maxZoom={18}
          updateWhenIdle
          updateWhenZooming={false}
          keepBuffer={4}
        />
      )}

      {layers.bathymetry && (
        <CircleMarker center={DEFAULT_CENTER} radius={240} pathOptions={{ color: "#0284c7", fillColor: "#38bdf8", fillOpacity: 0.08, opacity: 0.2 }} />
      )}

      {layers.grid && <GridOverlay />}

      {gps && layers.gpsAccuracy && <Circle center={gps.point} radius={gps.accuracy || 35} pathOptions={{ color: accuracyColor, weight: 1, fillColor: accuracyColor, fillOpacity: 0.08, opacity: 0.55, dashArray: gps.isEstimated ? "6 6" : undefined }} />}
      {gps && layers.distanceRings && [250, 500, 1000].map((radius) => <Circle key={radius} center={gps.point} radius={radius} pathOptions={{ color: "#0f766e", weight: 1, fillOpacity: 0, opacity: 0.25 }} />)}
      {vesselTrace.length > 1 && <Polyline positions={vesselTrace} pathOptions={{ color: "#0f172a", weight: 2, opacity: 0.65, dashArray: "3 7" }} />}
      {gps && layers.vesselPosition !== false && (
        <Marker position={gps.point} icon={vesselIcon}>
          <Popup>{gps.isEstimated ? "Estimated vessel location" : "Your vessel location"}</Popup>
        </Marker>
      )}
      {layers.courseLine && courseLine && <Polyline positions={courseLine} pathOptions={{ color: "#22d3ee", weight: 3, opacity: 0.9, dashArray: "8 8" }} />}
      {layers.headingLine && headingLine && <Polyline positions={headingLine} pathOptions={{ color: "#f59e0b", weight: 3, opacity: 0.9 }} />}
      {layers.bearingLine && gps && routePoints[0] && <Polyline positions={[gps.point, routePoints[0]]} pathOptions={{ color: "#ef4444", weight: 3, opacity: 0.85 }} />}

      {layers.marinas && marinas.map((marina) => (
        <CircleMarker key={marina.id} center={[marina.latitude, marina.longitude]} radius={7} pathOptions={{ color: "white", weight: 2, fillColor: "#14b8a6", fillOpacity: 1 }}>
          <Popup>
            <div className="max-w-64 space-y-2">
              <strong className="text-sm text-slate-950">{marina.name}</strong>
              <p className="text-xs text-slate-600">{marina.location || marina.city || marina.country || "Marstick marina"}</p>
              {marina.description && <p className="text-xs leading-5 text-slate-600">{String(marina.description).slice(0, 180)}</p>}
              {Array.isArray(marina.facilities) && marina.facilities.length > 0 && <p className="text-xs font-semibold text-teal-700">{marina.facilities.slice(0, 5).join(" · ")}</p>}
            </div>
          </Popup>
        </CircleMarker>
      ))}

      {layers.route && activeRoutePoints.length > 1 && <Polyline positions={activeRoutePoints} pathOptions={{ color: "#14b8a6", weight: 4, opacity: 0.9 }} />}
      {layers.route && layers.waypoints !== false && routePoints.map((point, index) => (
        <Marker key={`${point[0]}-${point[1]}-${index}`} position={point} icon={PENCIL_DOT_MARKER_ICON}>
          <Popup>
            <div className="space-y-2 text-center">
              <p className="text-xs font-bold text-slate-800">Waypoint {index + 1}</p>
              <button
                type="button"
                onClick={() => onDeleteWaypoint?.(index)}
                className="rounded-full bg-red-600 px-3 py-1.5 text-xs font-bold text-white shadow hover:bg-red-700"
              >
                Delete waypoint
              </button>
            </div>
          </Popup>
        </Marker>
      ))}

      <MapResizeFix />
      <UserLocationCenter point={gps?.point} />
      <CenterOnUserAction point={gps?.point} requestKey={centerRequestKey} />
      <MapEvents layers={layers} onCursor={onCursor} onAddWaypoint={onAddWaypoint} />
    </MapContainer>
  );
}